import { Award, ShieldCheck, MapPin, ArrowRight } from "lucide-react";
import { Link } from "@tanstack/react-router";
import { ThreeMBadge } from "@/components/ThreeMBadge";

/**
 * Prominent visual statement of BBN Nordic's auktoriserade 3M-installatör
 * status. Placed directly under the hero on the homepage and reused on
 * /3m-prestige and /om-oss. Markus wants this claim front and center.
 */
export function OfficialDealerBanner({
  variant = "section",
}: {
  variant?: "section" | "compact";
} = {}) {
  return (
    <section
      aria-label="Officiell auktoriserad 3M-installatör i Sverige sedan 2008"
      className={`relative overflow-hidden bg-navy text-cream ${
        variant === "section" ? "border-y border-gold/25" : "border border-gold/25 rounded-2xl"
      }`}
    >
      {/* Decorative glow */}
      <div
        aria-hidden
        className="absolute -top-32 left-1/2 -translate-x-1/2 w-[680px] h-[680px] rounded-full bg-gold/10 blur-3xl pointer-events-none"
      />
      <div
        aria-hidden
        className="absolute inset-y-0 left-0 w-1 bg-gradient-to-b from-transparent via-gold to-transparent"
      />
      <div
        aria-hidden
        className="absolute inset-y-0 right-0 w-1 bg-gradient-to-b from-transparent via-gold to-transparent"
      />

      <div className="relative mx-auto max-w-7xl px-5 md:px-10 py-10 md:py-14">
        <div className="grid lg:grid-cols-[auto_1fr_auto] gap-8 lg:gap-10 items-center">
          {/* Badge */}
          <div className="flex flex-col items-start gap-3">
            <div className="scale-[1.35] origin-left">
              <ThreeMBadge variant="dark" />
            </div>
            <span className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-gold-bright/90">
              <Award size={12} /> Officiell auktorisation
            </span>
          </div>

          {/* Statement */}
          <div className="lg:border-l lg:border-r lg:border-gold/20 lg:px-10">
            <p className="font-mono text-[11px] uppercase tracking-[0.3em] text-gold-bright mb-3">
              Auktoriserade sedan 2008
            </p>
            <h2 className="font-display text-2xl md:text-3xl lg:text-[2.4rem] leading-[1.05] uppercase tracking-tight text-cream">
              <span className="text-gold-bright">Officiella auktoriserade 3M-installatörer</span> i Sverige,{" "}
              <span className="whitespace-nowrap">sedan 2008</span>.
            </h2>
            <p className="mt-4 text-cream/75 text-sm md:text-base font-light leading-relaxed max-w-2xl">
              3M Svenska AB har utfärdat vår auktorisation direkt. Det är den enda certifieringen
              som ger rätt att lämna 3M:s fulla produktgaranti (10–15 år) på installationen.
              För att 3M-garantin ska gälla behöver du anlita en auktoriserad installatör.
            </p>
            <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs font-mono uppercase tracking-[0.18em] text-cream/75">
              <span className="inline-flex items-center gap-1.5">
                <ShieldCheck size={13} className="text-gold-bright" /> 10 års produktgaranti
              </span>
              <span className="inline-flex items-center gap-1.5">
                <Award size={13} className="text-gold-bright" /> Auktoriserad sedan 2008
              </span>
              <span className="inline-flex items-center gap-1.5">
                <MapPin size={13} className="text-gold-bright" /> Verksamma i hela Sverige
              </span>
            </div>
          </div>

          {/* CTA */}
          <div className="flex flex-col gap-3">
            <Link
              to="/auktoriserad-installator"
              className="inline-flex items-center justify-center gap-2 border border-gold/50 bg-gold/10 hover:bg-gold hover:text-navy text-gold-bright px-5 py-3 rounded-sm font-mono text-[11px] uppercase tracking-[0.18em] transition-colors group"
            >
              Se vår 3M-auktorisation
              <ArrowRight size={13} className="transition-transform group-hover:translate-x-0.5" />
            </Link>
            <Link
              to="/om-oss"
              className="inline-flex items-center justify-center gap-2 text-cream/70 hover:text-gold-bright px-5 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition-colors"
            >
              Läs om BBN Nordic
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}
